import {
    getShopBuilderBlockDefinition,
    type ShopBuilderBlockDefinition,
    type ShopBuilderInspectorField,
} from "../builderSchema";
import type { ShopTemplateBlock } from "../types";

export const LAGUNA_REGION_OPTION = "lagunaRegion";
export const LAGUNA_RUNTIME_GRID_ITEM_COUNT =
    "__lagunaRuntimeGridItemCount";
export const LAGUNA_RUNTIME_SOURCE_INDEX = "__lagunaRuntimeSourceIndex";

export const LAGUNA_RUNTIME_REGIONS = [
    "benefitStrip",
    "breadcrumb",
    "categoryHero",
    "categoryRail",
    "contactInfo",
    "footer",
    "header",
    "heroSlider",
    "newsletter",
    "productRail",
    "promoBar",
    "relatedProducts",
    "richContent",
    "seoText",
    "serviceBenefits",
    "systemAccountNavigation",
    "systemCartItems",
    "systemCartSummary",
    "systemCheckoutSteps",
    "systemContactForm",
    "systemProductBuyBox",
    "systemProductDescription",
    "systemProductGallery",
    "systemProductList",
    "systemProductSummary",
    "systemSearchForm",
    "systemSubcategoryList",
] as const;

export type LagunaRuntimeRegion = typeof LAGUNA_RUNTIME_REGIONS[number];

export const LAGUNA_HOME_RUNTIME_REGIONS = [
    "promoBar",
    "header",
    "heroSlider",
    "categoryRail",
    "productRail",
    "benefitStrip",
    "newsletter",
    "seoText",
    "footer",
] as const satisfies readonly LagunaRuntimeRegion[];

export type LagunaHomeRuntimeRegion =
    typeof LAGUNA_HOME_RUNTIME_REGIONS[number];

type LagunaInspectorPresentation = Readonly<{
    inspector?: readonly ShopBuilderInspectorField[];
    options: Readonly<Record<string, unknown>>;
    styleCapabilities: readonly string[];
}>;

const lagunaRuntimeRegionTypes = new Set<string>(LAGUNA_RUNTIME_REGIONS);
const lagunaHomeRuntimeRegionTypes = new Set<string>(
    LAGUNA_HOME_RUNTIME_REGIONS,
);

const lagunaInspectorPresentations: Readonly<
    Record<LagunaHomeRuntimeRegion, LagunaInspectorPresentation>
> = {
    promoBar: {
        options: {
            backgroundColor: "var(--site-primary)",
            fontFamily: "var(--site-body-font)",
            fontSize: "12px",
            fontWeight: "700",
            letterSpacing: "0px",
            lineHeight: "1.35",
            textAlign: "center",
            textColor: "var(--site-on-primary)",
        },
        styleCapabilities: ["typography", "color"],
    },
    header: {
        options: {
            backgroundColor: "var(--site-surface)",
            boxShadow: "soft",
            textColor: "var(--site-text)",
        },
        styleCapabilities: ["color", "surface"],
    },
    heroSlider: {
        inspector: [
            { key: "slide1ImageUrl", kind: "media", scope: "content" },
            { key: "slide2ImageUrl", kind: "media", scope: "content" },
            { key: "slide3ImageUrl", kind: "media", scope: "content" },
        ],
        options: {},
        styleCapabilities: [],
    },
    categoryRail: {
        options: {
            backgroundColor: "var(--site-background)",
            textColor: "var(--site-text)",
        },
        styleCapabilities: ["color"],
    },
    productRail: {
        options: {
            backgroundColor: "var(--site-surface)",
            textColor: "var(--site-text)",
        },
        styleCapabilities: ["color"],
    },
    benefitStrip: {
        options: {
            backgroundColor: "var(--site-primary)",
            textColor: "var(--site-on-primary)",
        },
        styleCapabilities: ["color"],
    },
    newsletter: {
        options: {
            backgroundColor: "var(--site-primary)",
            textColor: "var(--site-on-primary)",
        },
        styleCapabilities: ["color"],
    },
    seoText: {
        options: {
            backgroundColor: "var(--site-background)",
            textColor: "var(--site-text)",
        },
        styleCapabilities: ["color"],
    },
    footer: {
        options: {
            backgroundColor: "var(--site-inverse)",
            textColor: "var(--site-on-inverse)",
        },
        styleCapabilities: ["color"],
    },
};

function isLagunaRuntimeAnchor(block: ShopTemplateBlock): boolean {
    const options = block.options ?? {};
    const hasRuntimeMarker = (
        typeof options.lagunaRuntimeSource === "string"
        || typeof options.runtimeSource === "string"
        || typeof options.commerceRuntimeRole === "string"
    );
    const hasAuthoredContent = Object.keys(block.content ?? {}).length > 0;
    const hasAuthoredSlotContent = Object.values(block.slots ?? {})
        .some((children) => children.length > 0);

    return block.type === "column"
        && hasRuntimeMarker
        && !hasAuthoredContent
        && !hasAuthoredSlotContent;
}

function withLagunaRuntimeSourceIndex(
    block: ShopTemplateBlock,
    sourceIndex: number,
): ShopTemplateBlock {
    return {
        ...block,
        [LAGUNA_RUNTIME_SOURCE_INDEX]: sourceIndex,
    } as ShopTemplateBlock;
}

export function lagunaRuntimeSourceIndex(
    block: ShopTemplateBlock,
    fallbackIndex: number,
): number {
    const sourceIndex = (
        block as ShopTemplateBlock & {
            [LAGUNA_RUNTIME_SOURCE_INDEX]?: unknown;
        }
    )[LAGUNA_RUNTIME_SOURCE_INDEX];

    return typeof sourceIndex === "number"
        && Number.isInteger(sourceIndex)
        && sourceIndex >= 0
        ? sourceIndex
        : fallbackIndex;
}

export function lagunaRuntimeSlotItemCount(
    blocks: readonly ShopTemplateBlock[],
): number {
    return blocks.reduce((count, childBlock, index) => (
        Math.max(count, lagunaRuntimeSourceIndex(childBlock, index) + 1)
    ), 0);
}

export function lagunaRuntimeGridItemCount(
    block: ShopTemplateBlock | null | undefined,
): number {
    const itemCount = (
        block as (ShopTemplateBlock & {
            [LAGUNA_RUNTIME_GRID_ITEM_COUNT]?: unknown;
        }) | null | undefined
    )?.[LAGUNA_RUNTIME_GRID_ITEM_COUNT];

    return typeof itemCount === "number"
        && Number.isInteger(itemCount)
        && itemCount >= 0
        ? itemCount
        : 0;
}

export function lagunaRuntimeRegion(block: ShopTemplateBlock | null | undefined): string {
    const region = block?.options?.[LAGUNA_REGION_OPTION];

    return typeof region === "string" && lagunaRuntimeRegionTypes.has(region)
        ? region
        : "";
}

export function isLagunaRuntimeRegion(
    block: ShopTemplateBlock | null | undefined,
    region: string,
): boolean {
    return block?.type === "richContent" && lagunaRuntimeRegion(block) === region;
}

/**
 * Resolves the visual contract of a neutral Laguna root for the inspector.
 *
 * The persisted type intentionally remains richContent. This presentation-only
 * definition exposes the runtime region label, localization schema, and only
 * renderer-backed appearance controls. Library insertion and placement still
 * use the neutral richContent definition.
 */
export function getLagunaBuilderBlockInspectorDefinition(
    block: ShopTemplateBlock | null | undefined,
): ShopBuilderBlockDefinition | null {
    const region = lagunaRuntimeRegion(block);

    if (
        block?.type !== "richContent"
        || !lagunaHomeRuntimeRegionTypes.has(region)
    ) {
        return null;
    }

    const homeRegion = region as LagunaHomeRuntimeRegion;
    const presentation = lagunaInspectorPresentations[homeRegion];
    const storageDefinition = getShopBuilderBlockDefinition("richContent");
    const runtimeDefinition = getShopBuilderBlockDefinition(homeRegion);
    const inspectorFields = [
        ...storageDefinition.inspector,
        ...(presentation.inspector ?? []),
    ].filter((field, index, fields) => (
        fields.findIndex((candidate) => (
            candidate.key === field.key
            && candidate.scope === field.scope
        )) === index
    ));

    return {
        ...storageDefinition,
        contextualDefaults: {
            ...storageDefinition.contextualDefaults,
            options: {
                ...storageDefinition.contextualDefaults?.options,
                ...presentation.options,
            },
        },
        inspector: inspectorFields,
        localization: runtimeDefinition.localization,
        renderer: runtimeDefinition.renderer,
        styleCapabilities: [
            ...new Set([
                ...storageDefinition.styleCapabilities,
                ...presentation.styleCapabilities,
            ]),
        ],
        type: homeRegion,
    };
}

export function lagunaBuilderRegionBlock(
    region: LagunaRuntimeRegion,
    runtime: Readonly<{
        commerceRuntimeRole: string;
    }>,
): ShopTemplateBlock {
    return {
        type: "richContent",
        options: {
            [LAGUNA_REGION_OPTION]: region,
        },
        slots: {
            grid: [{
                type: "column",
                options: {
                    commerceRuntimeRole: runtime.commerceRuntimeRole,
                    documentRole: region,
                    gridH: 1,
                    gridW: 12,
                    gridX: 1,
                    gridY: 1,
                },
            }],
        },
    };
}

/**
 * Laguna stores neutral rich-content roots and inner columns. The region marker
 * selects an existing storefront runtime component without restoring a legacy
 * block type in the persisted builder document.
 */
export function lagunaRuntimeBlock(block: ShopTemplateBlock): ShopTemplateBlock {
    const region = lagunaRuntimeRegion(block);

    if (block.type !== "richContent" || region === "") {
        return block;
    }

    const { grid, ...contentSlots } = block.slots ?? {};
    const visibleGrid = region === "heroSlider"
        ? grid
        : grid?.flatMap((childBlock, sourceIndex) => (
            isLagunaRuntimeAnchor(childBlock)
                ? []
                : [withLagunaRuntimeSourceIndex(childBlock, sourceIndex)]
        ));
    const runtimeSlots = {
        ...contentSlots,
        ...(grid !== undefined ? { grid: visibleGrid ?? [] } : {}),
    };

    return {
        ...block,
        ...(grid !== undefined ? {
            [LAGUNA_RUNTIME_GRID_ITEM_COUNT]: grid.length,
        } : {}),
        type: region,
        slots: Object.keys(runtimeSlots).length > 0
            ? runtimeSlots
            : undefined,
    } as ShopTemplateBlock;
}
